import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Star, Car, Clock, ShieldCheck, MapPin, Plane, TrainFront, X,
  CheckCircle2, Loader2, MessageSquare, KeyRound, CalendarClock, Users, Briefcase,
} from "lucide-react";
import { toast } from "sonner";
import MapView from "@/components/MapView";
import Avatar from "@/components/Avatar";
import Button from "@/components/Button";
import BidCard from "@/components/BidCard";
import RatingStars from "@/components/RatingStars";
import VehicleImage from "@/components/VehicleImage";
import StripeCardModal from "@/components/StripeCardModal";
import Modal from "@/components/Modal";
import { Spinner } from "@/components/States";
import { api } from "@/lib/api";
import { money, miles, fmtDateTime, cn } from "@/lib/format";
import { speak, cancelVoice } from "@/lib/voice";

const STATUS_TEXT = {
  driver_enroute: "Your driver is on the way",
  arrived: "Your driver has arrived",
  in_progress: "Enjoy your ride",
  completed: "You've arrived!",
};

export default function Ride() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [ride, setRide] = useState(null);
  const [offers, setOffers] = useState([]);
  const [track, setTrack] = useState(null);
  const [status, setStatus] = useState("searching");
  const [selecting, setSelecting] = useState(null);
  const [error, setError] = useState(null);
  const [payMethod, setPayMethod] = useState({ enabled: false, has_card: false });
  const [pendingOffer, setPendingOffer] = useState(null);
  const [cardSecret, setCardSecret] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [tip, setTip] = useState(0);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingSent, setRatingSent] = useState(false);
  const [paying, setPaying] = useState(false);

  const statusRef = useRef(status);
  statusRef.current = status;
  const hasDriverRef = useRef(false);
  hasDriverRef.current = !!ride?.assigned_driver;
  const spokeStatusRef = useRef(null);
  const spokeNearRef = useRef(false);

  // Spoken trip notifications for the rider (like the mobile app).
  useEffect(() => {
    const msgs = {
      driver_enroute: "Your driver is on the way.",
      arrived: "Your driver has arrived. Please head to the pickup point.",
      in_progress: "Your trip has started. Enjoy your ride.",
      completed: "You've arrived at your destination.",
    };
    if (spokeStatusRef.current === null) { spokeStatusRef.current = status; return; }
    if (status && msgs[status] && spokeStatusRef.current !== status) {
      spokeStatusRef.current = status;
      speak(msgs[status]);
    }
  }, [status]);

  useEffect(() => {
    if (status === "in_progress" && track?.eta_minutes > 0 && track.eta_minutes <= 2 && !spokeNearRef.current) {
      spokeNearRef.current = true;
      speak("You're approaching your destination.");
    }
  }, [status, track?.eta_minutes]);

  useEffect(() => () => cancelVoice(), []);

  useEffect(() => {
    api(`/rides/${id}`).then((r) => { setRide(r.ride); setStatus(r.ride.status); setTip(r.ride.tip || 0); });
    api("/payments/method").then((m) => setPayMethod({ enabled: !!m.enabled, has_card: !!m.has_card })).catch(() => {});
  }, [id]);

  const tick = useCallback(async () => {
    try {
      const s = statusRef.current;
      if (s === "searching" || (s === "scheduled" && !hasDriverRef.current)) {
        const r = await api(`/rides/${id}/offers`);
        setOffers(r.offers || []);
      } else {
        const t = await api(`/rides/${id}/track`);
        setTrack(t);
        if (t.tip != null) setTip(t.tip);
        if (t.status && t.status !== statusRef.current) setStatus(t.status);
      }
    } catch { /* noop */ }
  }, [id]);

  useEffect(() => {
    const iv = setInterval(tick, 2500);
    tick();
    return () => clearInterval(iv);
  }, [tick]);

  const confirmSelect = async (offer) => {
    setSelecting(offer.id);
    setError(null);
    try {
      const r = await api(`/rides/${id}/select`, { method: "POST", body: { offer_id: offer.id } });
      setRide(r.ride);
      setStatus(r.ride.status);
      toast.success(`${offer.driver.name} is confirmed!`);
    } catch (e) {
      setError(e?.message || "Could not confirm this offer.");
      setSelecting(null);
    }
  };

  const accept = async (offer) => {
    setError(null);
    if (payMethod.enabled && !payMethod.has_card) {
      setPendingOffer(offer);
      setSelecting(offer.id);
      try {
        const r = await api("/payments/setup-intent", { method: "POST" });
        setCardSecret(r.client_secret);
      } catch (e) {
        setError(e?.message || "Could not start card setup.");
        setPendingOffer(null);
        setSelecting(null);
      }
      return;
    }
    await confirmSelect(offer);
  };

  const onCardSaved = async (setupIntentId) => {
    try {
      await api("/payments/setup-complete", { method: "POST", body: { setup_intent_id: setupIntentId } });
      setCardSecret(null);
      setPayMethod({ enabled: true, has_card: true });
      const offer = pendingOffer;
      setPendingOffer(null);
      if (offer) await confirmSelect(offer);
    } catch (e) {
      setError(e?.message || "Could not save your card.");
    }
  };

  const cancel = async () => {
    setCancelBusy(true);
    try {
      const r = await api(`/rides/${id}/cancel`, { method: "POST" });
      const fee = r?.cancellation_fee || 0;
      setConfirmCancel(false);
      if (fee > 0) toast.info(`A ${money(fee)} cancellation fee was charged.`);
      else toast.success("Ride cancelled.");
      navigate("/trips");
    } catch (e) {
      toast.error(e?.message || "Could not cancel this ride.");
    } finally {
      setCancelBusy(false);
    }
  };

  const addTip = async (amount) => {
    setTip(amount);
    try {
      const r = await api(`/rides/${id}/tip`, { method: "POST", body: { amount } });
      if (r?.charged) toast.success(`Tip of ${money(amount)} charged.`);
    } catch (e) {
      toast.error(e?.message || "Could not add tip.");
    }
  };

  const submitRating = async () => {
    if (!ratingStars) return;
    try {
      await api(`/rides/${id}/rate`, { method: "POST", body: { rating: ratingStars } });
      setRatingSent(true);
      toast.success("Thanks for rating your driver!");
    } catch (e) {
      toast.error(e?.message || "Could not submit rating.");
    }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const r = await api("/payments/checkout/session", { method: "POST", body: { ride_id: id, origin_url: window.location.origin } });
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.message || "Could not start payment.");
      setPaying(false);
    }
  };

  if (!ride) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Spinner className="h-8 w-8 text-brand-600" />
      </div>
    );
  }

  const showOffers = status === "searching" || (status === "scheduled" && !ride.assigned_driver);
  const scheduledConfirmed = status === "scheduled" && !!ride.assigned_driver;
  const completed = status === "completed";
  const tracking = !showOffers && !scheduledConfirmed && !completed;
  const driver = ride.assigned_driver || track?.assigned_driver;
  const driverLoc = track?.driver_location || driver?.start;
  const bestOfferId = offers.length > 1 ? offers.reduce((b, o) => (o.fare < b.fare ? o : b), offers[0]).id : null;
  const whenText = ride.when === "scheduled" ? (fmtDateTime(ride.scheduled_ts) || ride.scheduled_time) : "Ride now";
  const flight = ride.airport_info && (ride.airport_info.airline || ride.airport_info.flight)
    ? [ride.airport_info.airline, ride.airport_info.flight].filter(Boolean).join(" ") : null;

  return (
    <div className="flex h-screen flex-col bg-background md:flex-row">
      {/* Map */}
      <div className="relative h-[38vh] w-full shrink-0 md:h-full md:flex-1">
        <MapView
          pickup={ride.pickup}
          destination={ride.destination}
          stops={ride.stops}
          forceDark
          ambientCars={showOffers || scheduledConfirmed ? 7 : 0}
          driver={!showOffers && !scheduledConfirmed ? driverLoc : null}
          driverPulse={status === "arrived" && !!driverLoc}
          trackTo={driverLoc ? (status === "in_progress" ? ride.destination : (status === "driver_enroute" ? ride.pickup : null)) : null}
          showRoute={!tracking}
        />
        <button onClick={() => navigate("/")} className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-card text-foreground shadow-card" data-testid="ride-back">
          <ChevronLeft className="h-5 w-5" />
        </button>
      </div>

      {/* Panel */}
      <div className="flex w-full flex-col overflow-y-auto border-t border-border bg-background md:w-[440px] md:border-l md:border-t-0">
        <div className="flex-1 p-5">
          {scheduledConfirmed && driver && (
            <p className="mb-4 text-lg font-bold text-black">Your ride is confirmed. See driver details below!</p>
          )}
          {tracking && (
            <div className="mb-4 flex items-start justify-between gap-3">
              <h2 className="text-2xl font-extrabold text-foreground">{STATUS_TEXT[status] || "On the way"}</h2>
              {track?.eta_minutes > 0 && status !== "arrived" && (
                <div className="shrink-0 text-right">
                  <p className="text-xs font-medium text-muted-foreground">{status === "in_progress" ? "Dropoff ETA" : "Driver ETA"}</p>
                  <p className="font-num text-xl font-extrabold text-foreground" data-testid="dropoff-eta">{track.eta_minutes} min</p>
                </div>
              )}
            </div>
          )}
          {/* Route summary */}
          <div className="mb-4 rounded-2xl bg-secondary p-3">
            <div className="flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1.5 truncate">
                {ride.pickup.airport ? <Plane className="h-4 w-4 text-brand-600" /> : ride.pickup.station ? <TrainFront className="h-4 w-4 text-brand-600" /> : <MapPin className="h-4 w-4 text-success" />}
                <span className="truncate text-foreground">{ride.pickup.label}</span>
              </span>
            </div>
            <div className="my-0.5 ml-2 h-3 border-l-2 border-dashed border-border" />
            <div className="flex items-center gap-2 text-sm">
              {ride.destination.airport ? <Plane className="h-4 w-4 text-brand-600" /> : ride.destination.station ? <TrainFront className="h-4 w-4 text-brand-600" /> : <MapPin className="h-4 w-4 text-brand-600" />}
              <span className="truncate text-foreground">{ride.destination.label}</span>
            </div>
            {ride.distance_miles != null && (
              <p className="mt-1.5 text-xs text-muted-foreground">{miles(ride.distance_miles)} · {ride.required_class_label || "Economy"}</p>
            )}
            <div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-border/60 pt-2.5">
              <span className="flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-semibold text-foreground">
                <CalendarClock className="h-3.5 w-3.5 text-brand-600" /> {whenText}
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-semibold text-foreground">
                <Users className="h-3.5 w-3.5 text-brand-600" /> {ride.passengers ?? 1} pax
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-semibold text-foreground">
                <Briefcase className="h-3.5 w-3.5 text-brand-600" /> {ride.bags ?? 0} bags
              </span>
              {ride.duration_min != null && (
                <span className="flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-semibold text-foreground">
                  <Clock className="h-3.5 w-3.5 text-brand-600" /> {ride.duration_min} min
                </span>
              )}
              {flight && (
                <span className="flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1 text-xs font-semibold text-foreground">
                  <Plane className="h-3.5 w-3.5 text-brand-600" /> {flight}
                </span>
              )}
            </div>
          </div>

          {/* OFFERS */}
          {showOffers && (
            <>
              <div className="mb-3 flex items-center gap-2">
                <h2 className="text-xl font-extrabold text-foreground">Choose your driver</h2>
              </div>
              {offers.length === 0 ? (
                <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-card p-8 text-center">
                  <Loader2 className="h-7 w-7 animate-spin text-brand-600" />
                  <p className="text-sm font-medium text-muted-foreground">Finding drivers near you…</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {offers.map((o) => (
                    <BidCard key={o.id} offer={o} onAccept={accept} loading={selecting === o.id} disabled={!!selecting && selecting !== o.id} best={o.id === bestOfferId} />
                  ))}
                </div>
              )}
              {error && <p className="mt-3 text-sm font-medium text-destructive">{error}</p>}
              <button onClick={() => setConfirmCancel(true)} className="mt-4 w-full text-center text-sm font-semibold text-muted-foreground">Cancel request</button>
            </>
          )}

          {/* SCHEDULED CONFIRMED */}
          {scheduledConfirmed && driver && (
            <>
              <DriverCard driver={driver} />
              <button onClick={() => setConfirmCancel(true)} className="mt-4 w-full text-center text-sm font-semibold text-destructive">Cancel scheduled ride</button>
            </>
          )}

          {/* COMPLETED */}
          {completed && (
            <>
              <div className="mb-4 flex flex-col items-center rounded-2xl bg-accent p-6 text-center">
                <CheckCircle2 className="h-10 w-10 text-success" />
                <h2 className="mt-2 text-2xl font-extrabold text-foreground">You&apos;ve arrived!</h2>
                <p className="font-num mt-1 text-3xl font-bold text-foreground">{money((ride.final_fare || 0) + (tip || 0))}</p>
                <p className="text-xs text-muted-foreground">Fare {money(ride.final_fare)}{tip ? ` + tip ${money(tip)}` : ""}</p>
                <span className={cn("mt-2 rounded-full px-3 py-1 text-xs font-semibold", (track?.payment_status || ride.payment_status) === "paid" ? "bg-success/15 text-success" : "bg-secondary text-muted-foreground")}>
                  {(track?.payment_status || ride.payment_status) === "paid" ? "Paid" : "Payment pending"}
                </span>
              </div>

              {driver && <DriverCard driver={driver} className="mb-4" />}

              {/* Tip */}
              <div className="mb-4 rounded-2xl border border-border bg-card p-4">
                <p className="mb-3 font-bold text-foreground">Add a tip for {driver?.name?.split(" ")[0] || "your driver"}</p>
                <div className="flex gap-2">
                  {[3, 5, 8, 10].map((amt) => (
                    <button key={amt} data-testid={`tip-${amt}`} onClick={() => addTip(amt)} className={cn("flex-1 rounded-xl border-2 py-2.5 font-num font-bold transition-colors", tip === amt ? "border-brand-600 bg-accent text-brand-700 dark:text-brand-400" : "border-border text-foreground")}>
                      ${amt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Rating */}
              <div className="mb-4 rounded-2xl border border-border bg-card p-4">
                <p className="mb-3 font-bold text-foreground">Rate your ride</p>
                {ratingSent ? (
                  <p className="flex items-center gap-2 text-sm font-medium text-success"><CheckCircle2 className="h-4 w-4" /> Thanks for your feedback!</p>
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <RatingStars value={ratingStars} onChange={setRatingStars} />
                    <Button className="w-full" onClick={submitRating} disabled={!ratingStars} data-testid="submit-rating">Submit rating</Button>
                  </div>
                )}
              </div>

              {(track?.payment_status || ride.payment_status) !== "paid" && !payMethod.enabled && (
                <Button className="mb-3 w-full" onClick={payNow} loading={paying} data-testid="pay-now">Pay {money((ride.final_fare || 0) + (tip || 0))}</Button>
              )}
              <Button variant="secondary" className="w-full" onClick={() => navigate("/")}>Book another ride</Button>
            </>
          )}

          {/* TRACKING */}
          {!showOffers && !scheduledConfirmed && !completed && (
            <>
              {driver && <DriverCard driver={driver} className="mb-4" onMessage={status === "in_progress" ? undefined : () => navigate(`/chat/${id}`)} />}

              {status === "arrived" && ride.start_pin && (
                <div className="mb-4 flex items-center gap-3 rounded-2xl border-2 border-brand-500/50 bg-accent p-4">
                  <KeyRound className="h-6 w-6 text-brand-600" />
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Show your driver this start PIN</p>
                    <p className="font-num text-2xl font-extrabold tracking-widest text-brand-700 dark:text-brand-400" data-testid="start-pin">{ride.start_pin}</p>
                  </div>
                </div>
              )}

              {status !== "in_progress" && (
                <button onClick={() => setConfirmCancel(true)} className="w-full text-center text-sm font-semibold text-destructive" data-testid="cancel-ride">Cancel ride</button>
              )}
            </>
          )}
        </div>
      </div>

      <StripeCardModal
        open={!!cardSecret}
        clientSecret={cardSecret}
        fare={pendingOffer?.fare}
        driverName={pendingOffer?.driver?.name}
        onSaved={onCardSaved}
        onClose={() => { setCardSecret(null); setPendingOffer(null); setSelecting(null); }}
      />

      <Modal open={confirmCancel} onClose={() => setConfirmCancel(false)} title="Cancel this ride?" size="sm"
        footer={
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => setConfirmCancel(false)}>Keep ride</Button>
            <Button variant="danger" className="flex-1" onClick={cancel} loading={cancelBusy} data-testid="confirm-cancel">Cancel ride</Button>
          </div>
        }>
        <p className="text-sm text-muted-foreground">
          {status === "driver_enroute" || status === "arrived"
            ? "Your driver is on the way — a $5.00 cancellation fee may apply."
            : "Are you sure you want to cancel this ride?"}
        </p>
      </Modal>
    </div>
  );
}

function DriverCard({ driver, className = "", onMessage }) {
  return (
    <div className={cn("rounded-2xl border border-border bg-card p-4", className)}>
      <div className="flex items-center gap-3">
        <Avatar src={driver.photo} name={driver.name} size={52} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-bold text-foreground">{driver.name}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-0.5"><Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {Number(driver.rating || 5).toFixed(1)}</span>
            <span>·</span>
            <span className="truncate">{[driver.color, driver.vehicle].filter(Boolean).join(" ")}</span>
          </div>
        </div>
        <VehicleImage desc={`${driver.color || ""} ${driver.vehicle || ""}`.trim()} className="h-12 w-20 rounded-lg" iconSize={22} testID="track-vehicle" />
        {onMessage && (
          <button onClick={onMessage} data-testid="message-driver" className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-brand-600">
            <MessageSquare className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-xl bg-secondary px-3 py-2 text-sm">
        <span className="flex items-center gap-1.5 text-muted-foreground"><Car className="h-4 w-4" /> {driver.plate || "—"}</span>
        <span className="flex items-center gap-1.5 text-success"><ShieldCheck className="h-4 w-4" /> Verified</span>
      </div>
    </div>
  );
}
